<template>
<div>
  <v-btn
    class="orders-cart-button"
    icon
    small
    @click="$store.commit('setShowSiloContentReportsOrdersCart', true)"
  >
    <i class="material-icons-outlined">shopping_cart</i>
    <span class="orders-cart-count" v-if="!loading && orders?.length">{{ orders.length }}</span>
  </v-btn>

  <div v-if="$store.state.showSiloContentReportsOrdersCart" class="modal is-active is-sticky-right silo-reports-cart-modal">
    <div class="modal-background" @click.stop="$store.commit('setShowSiloContentReportsOrdersCart', false)"></div>
    <div class="modal-content" style="width: 687px; max-width: 95vw;">
      <div class="box modal-container mainColor--text">
        <header class="cart-header">
          <div>
            <h2 class="cart-header__title">Cart</h2>
            <p v-if="cartItemCountText" class="cart-header__count">{{ cartItemCountText }}</p>
          </div>
          <button
            type="button"
            class="cart-header__close"
            aria-label="Close cart"
            @click="$store.commit('setShowSiloContentReportsOrdersCart', false)"
          >
            <i class="material-icons">close</i>
          </button>
        </header>

        <div class="main-part">
          <template v-if="!loading">
            <template v-if="invoices.length">
              <div class="empty-cart">
                <div class="empty-cart-main">
                  <h3 class="mb-10">You have an open invoice</h3>
                  <div>
                    <v-btn
                      rounded
                      color="profileAnchorColor"
                      class="button-with-icon text-none whiteColor--text padding8 height38 mb-6"
                      target="_blank"
                      :href="invoices[0].stripe_hosted_invoice_url"
                    >
                      <i class="material-icons-outlined" style="margin-right: 10px; font-size: 1.2rem;">shopping_basket</i>
                      Go to invoice payment ({{formatCurrency(invoices[0].stripe_hosted_invoice_amount)}} USD)
                    </v-btn>
                    <div class="mb-6">
                      or
                    </div>
                    <v-btn
                      rounded
                      class="button-with-icon text-none padding8 height38"
                      tag="a"
                      @click="closeCartInvoice(invoices[0])"
                    >
                      Close invoice without paying
                    </v-btn>
                  </div>
                </div>
                <!--
                <div v-if="siloDashboardUrl" class="empty-cart-form-controls">
                  <v-btn
                    rounded
                    block
                    large
                    color="profileAnchorColor"
                    class="text-none whiteColor--text"
                    @click="$router.push(siloDashboardUrl); $store.commit('setShowSiloContentReportsOrdersCart', false)"
                  >
                    <span>Select silos</span>
                  </v-btn>
                </div>
                -->
              </div>
            </template>
            <template v-else-if="orders.length">
              <div class="orders-list">
                <div
                  v-for="group in ordersGroupedBySite"
                  :key="group.label"
                  class="cart-site-group"
                >
                  <div class="cart-domain-heading">{{ group.label }}</div>
                  <div
                    v-for="order in group.orders"
                    :key="`${order.type}-${order.id}`"
                    class="cart-line-row"
                    :class="{ 'cart-line-row--no-status': !(isWhiteGloveUser && isMissionControlSession) }"
                  >
                    <div class="cart-line-row__main">
                      <div class="cart-line-title">{{ getOrderTitle(order) }}</div>
                      <div class="cart-line-subtitle">{{ getOrderSubtitle(order) }}</div>
                    </div>
                    <div
                      v-if="isWhiteGloveUser && isMissionControlSession"
                      class="cart-line-row__status"
                    >
                      <b-dropdown
                        aria-role="list"
                        class="cart-sales-tag-dropdown"
                        @click.native.stop
                      >
                        <template #trigger>
                          <button type="button" class="cart-sales-tag-trigger">
                            <span>{{ salesTagButtonLabel(order) }}</span>
                            <i class="material-icons-outlined cart-sales-tag-trigger__caret">expand_more</i>
                          </button>
                        </template>
                        <b-dropdown-item
                          :disabled="order.sales_tag === 'passive'"
                          aria-role="listitem"
                          @click.native.stop="updateOrder(order, { sales_tag: 'passive' })"
                        >
                          Set as passive
                        </b-dropdown-item>
                        <b-dropdown-item
                          :disabled="order.sales_tag === 'active'"
                          aria-role="listitem"
                          @click.native.stop="updateOrder(order, { sales_tag: 'active' })"
                        >
                          Set as <b>active</b>
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                    <div class="cart-line-row__price-actions">
                      <span class="cart-line-price">{{ formatCurrency(getOrderPrice(order)) }}</span>
                      <button
                        type="button"
                        class="cart-line-delete"
                        aria-label="Remove from cart"
                        @click="updateOrder(order, { set_user_editing: true })"
                      >
                        <i class="material-icons-outlined">delete</i>
                      </button>
                    </div>
                  </div>
                  <div class="cart-subtotal-bar">
                    <span>Sub-Total</span>
                    <span>{{ formatCurrency(getGroupSubtotal(group.orders)) }}</span>
                  </div>
                </div>
              </div>

              <div class="form-controls cart-form-footer">
                <div
                  v-if="isWhiteGloveUser && isMissionControlSession && new Set(orders.map((o) => o.sales_tag)).size >= 2"
                  class="cart-bulk-sales"
                >
                  <span class="cart-bulk-sales__label">Set all orders as</span>
                  <v-btn
                    class="sales-tag-btn cart-bulk-sales__btn"
                    small
                    @click="setAllSalesTags('passive')"
                  >
                    Passive
                  </v-btn>
                  <span class="cart-bulk-sales__sep">/</span>
                  <v-btn
                    class="sales-tag-btn cart-bulk-sales__btn"
                    small
                    color="success"
                    @click="setAllSalesTags('active')"
                  >
                    Active
                  </v-btn>
                </div>

                <div v-if="isRecurringApplicable" class="cart-recurring-block">
                  <div class="cart-section-label">Select payment frequency</div>
                  <div class="payment-frequency-options">
                    <b-radio
                      class="custom-radio-color"
                      v-model="payment_frequency"
                      name="payment_frequency"
                      native-value="one_time"
                      type="is-danger"
                    >One-time payment</b-radio>
                    <b-radio
                      class="custom-radio-color"
                      v-model="payment_frequency"
                      name="payment_frequency"
                      native-value="recurring"
                      type="is-danger"
                    >Recurring payment</b-radio>
                  </div>
                </div>

                <div class="cart-total-section">
                  <p class="cart-total-section__count">{{ cartItemCountText }}</p>
                  <div class="cart-total-row">
                    <span>In total</span>
                    <span>{{ formatCurrency(getTotalPrice()) }}</span>
                  </div>
                  <div
                    v-if="payment_frequency === 'recurring'"
                    class="cart-total-row cart-total-row--secondary"
                  >
                    <span>Due today</span>
                    <span>{{ formatCurrency(getDueTodayOrders()) }}</span>
                  </div>
                  <div class="cart-total-section__rule"></div>
                </div>

                <p class="cart-footer-note">{{ cartDeliveryNote }}</p>

                <div v-if="isBalancePaymentApplicable" class="cart-balance-block">
                  <div class="cart-section-label">Select your payment method</div>
                  <div class="payment-method-options">
                    <b-radio
                      class="custom-radio-color"
                      v-model="payment_method"
                      name="payment_method"
                      native-value="card"
                    >Credit card</b-radio>
                    <b-radio
                      class="custom-radio-color"
                      v-model="payment_method"
                      name="payment_method"
                      native-value="balance"
                    >{{ $store.getters.whiteGloveFamilyProgramName }} balance (current balance {{ formatCurrency(ownerUser.balance_cents / 100) }})</b-radio>
                  </div>
                </div>

                <v-btn
                  v-if="payment_frequency === 'one_time' && payment_method === 'balance'"
                  rounded
                  block
                  depressed
                  class="cart-primary-btn text-none"
                  :disabled="isMissionControlSession"
                  @click="showBalancePaymentConfirmModal = true"
                >
                  <i class="material-icons-outlined cart-primary-btn__icon">shopping_cart</i>
                  <span>Pay with balance – {{ formatCurrency(getTotalPrice()) }}</span>
                </v-btn>
                <template
                  v-if="isMissionControlSession"
                >
                  <v-btn
                    rounded
                    block
                    depressed
                    class="cart-primary-btn text-none"
                    @click="showSendInvoiceConfirmModal = true"
                  >
                    <i class="material-icons-outlined cart-primary-btn__icon">shopping_cart</i>
                    <span>Send invoice – {{ formatCurrency(getInvoiceAmount()) }}</span>
                  </v-btn>
                  <v-btn
                    v-if="payment_frequency === 'one_time' && orders.every(o => o.type === 'audit_order' || o.type === 'silo_order' || o.type === 'quick_content_order')"
                    rounded
                    block
                    depressed
                    class="cart-primary-btn text-none mt-6"
                    href="#" @click.prevent="showSetPaidConfirmModal = true"
                  ><span>Set all as paid</span></v-btn>
                </template>
                <v-btn
                  v-else
                  rounded
                  block
                  depressed
                  class="cart-primary-btn text-none"
                  :disabled="isMissionControlSession"
                  @click="checkout()"
                >
                  <i class="material-icons-outlined cart-primary-btn__icon">shopping_cart</i>
                  <span>Go to checkout – {{ formatCurrency(getTotalPrice()) }}</span>
                </v-btn>

                <div
                  v-if="payment_frequency === 'one_time'"
                  class="cart-coupon-block"
                >
                  <p
                    v-if="appliedCoupon"
                    class="cart-coupon-applied"
                  >
                    <i class="material-icons cart-coupon-applied__icon">check</i>
                    Coupon applied ({{ appliedCoupon.coupon.couponType === 'Amount Off' ? '$' : '' }}{{ appliedCoupon.coupon.amountOff }}{{ appliedCoupon.coupon.couponType === 'Percent Off' ? '%' : '' }} off)
                  </p>
                  <template v-else>
                    <button
                      v-if="!showCouponInput"
                      type="button"
                      class="cart-coupon-link"
                      @click="showCouponInput = true"
                    >
                      Apply a coupon
                    </button>
                    <div v-else class="cart-coupon-fields">
                      <v-text-field
                        v-model="modalCouponFieldValue"
                        outlined
                        dense
                        hide-details
                        placeholder="Enter coupon"
                        color="grayColor"
                        class="cart-coupon-fields__input"
                      />
                      <v-btn
                        depressed
                        class="cart-coupon-fields__apply text-none whiteColor--text"
                        :disabled="!modalCouponFieldValue"
                        @click.stop="applyCoupon()"
                      >
                        Apply
                      </v-btn>
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="empty-cart">
                <div class="empty-cart-main">
                  <h3 class="mb-3">Your cart is empty</h3>
                  <p>{{ $store.getters.isPopAuthorityUser ? 'As a POP Authority user' : 'As a white glove POP user' }}, you can order content for your keywords and audits for your sites.</p>
                </div>
                <!--
                <div v-if="siloDashboardUrl" class="empty-cart-form-controls">
                  <v-btn
                    rounded
                    block
                    large
                    color="profileAnchorColor"
                    class="text-none whiteColor--text"
                    @click="$router.push(siloDashboardUrl); $store.commit('setShowSiloContentReportsOrdersCart', false)"
                  >
                    <span>Select silos</span>
                  </v-btn>
                </div>
                -->
              </div>
            </template>
          </template>
          <template v-else>
            <div class="loading-cart">
              <div>
                <b-icon
                  pack="fa"
                  icon="spinner"
                  custom-class="fa-spin">
                </b-icon>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showSendInvoiceConfirmModal && isMissionControlSession"
    class="modal"
    :class="{
      'is-active': true,
    }"
  >
    <div
      class="modal-background"
      @click="
        showSendInvoiceConfirmModal = false
      "
    ></div>
    <div class="modal-content">
      <div class="box modal-container">
        <div class="pad25">
          <div>
            <div
              class="mainColor--text font-weight-bold mb-5"
              style="font-size: 20px"
            >
              Are you sure?
            </div>
            <div
              class="mainColor--text mb-5"
              style="font-size: 16px; line-height: 24px"
            >
              You are about to send an invoice by email to the site owner's billing email (<b style="font-size: 16px; line-height: 24px">{{ selectedSubAccountRelation?.agencyInvoicingEmail || selectedSubAccountRelation?.agencyUserEmail }}</b>).
              <br /><br />
              Are you sure you want to proceed?
            </div>
          </div>
          <div class="text-right">
            <v-btn
              rounded
              color="profileAnchorColor"
              class="button-with-icon whiteColor--text text-none ml-2"
              @click="checkout('invoice')"
            >
              <span>Yes, send invoice ({{ formatCurrency(this.getInvoiceAmount()) }} USD)</span>
            </v-btn>
            <v-btn
              rounded
              outlined
              color="mainColor"
              class="button-with-icon mainColor--text text-none ml-2"
              @click="
                () => {
                  showSendInvoiceConfirmModal = false;
                }
              "
            >
              <span>Cancel</span>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showSetPaidConfirmModal && isMissionControlSession"
    class="modal"
    :class="{
      'is-active': true,
    }"
  >
    <div
      class="modal-background"
      @click="
        showSetPaidConfirmModal = false
      "
    ></div>
    <div class="modal-content">
      <div class="box modal-container">
        <div class="pad25">
          <div>
            <div
              class="mainColor--text font-weight-bold mb-5"
              style="font-size: 20px"
            >
              Are you sure?
            </div>
            <div
              class="mainColor--text mb-5"
              style="font-size: 16px; line-height: 24px"
            >
              This will set all cart orders as paid and create Asana tasks.
            </div>
          </div>
          <div class="text-right">
            <v-btn
              rounded
              color="profileAnchorColor"
              class="button-with-icon whiteColor--text text-none ml-2"
              @click="setAllAsPaid()"
            >
              <span>Yes, proceed</span>
            </v-btn>
            <v-btn
              rounded
              outlined
              color="mainColor"
              class="button-with-icon mainColor--text text-none ml-2"
              @click="
                () => {
                  showSetPaidConfirmModal = false;
                }
              "
            >
              <span>Cancel</span>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showBalancePaymentConfirmModal"
    class="modal"
    :class="{
      'is-active': true,
    }"
  >
    <div
      class="modal-background"
      @click="
        showBalancePaymentConfirmModal = false
      "
    ></div>
    <div class="modal-content">
      <div class="box modal-container">
        <div class="pad10">
          <div>
            <div
              class="mainColor--text font-weight-bold mb-5"
              style="font-size: 20px"
            >
              Confirm payment
            </div>
            <div
              class="mainColor--text mb-5"
              style="font-size: 16px; line-height: 24px"
            >
              You’re about to use <b>{{ formatCurrency(this.getTotalPrice()) }}</b> from your {{ $store.getters.whiteGloveFamilyProgramName }} balance to order content services from your POP team. This amount will be deducted immediately from your available balance.
            </div>
          </div>

          <div class="d-flex align-center justify-space-between gap10">
            <div
              class="mainColor--text"
              style="font-size: 16px; line-height: 24px; font-weight: 700;"
            >
              Current balance: {{ formatCurrency(this.ownerUser.balance_cents / 100) }}
            </div>
            <div class="text-right">
              <v-btn
                outlined
                color="mainColor"
                class="button-with-icon mainColor--text text-none ml-2"
                @click="
                  () => {
                    showBalancePaymentConfirmModal = false;
                  }
                "
              >
                <span>Cancel</span>
              </v-btn>
              <v-btn
                color="profileAnchorColor"
                class="button-with-icon whiteColor--text text-none ml-2"
                @click="checkout('balance')"
              >
                <span>Confirm & order</span>
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</div>
</template>

<script>
import { sortBy, groupBy } from 'lodash'
import { getPureServicePkgName, formatCurrency } from '@/utils';
import {
  whiteGlovePlans,
  ALLOWED_SILO_ORDER_DELIVERY_PERIODS,
  ALLOWED_SILO_ORDER_DELIVERY_PERIODS_FOR_DUE_NOW,
  SILO_ORDER_SPEEDY_DELIVERY_PERIOD,
  SILO_ORDER_SPEEDY_DELIVERY_MULTIPLIER,
  AVAILABLE_BALANCE_TOP_UP_OPTIONS
} from "@/components/plans_constants"


export default {
  props: [],
  components: {

  },
  data() {
    return {
      loading: false,
      packages: [],
      orders: [],
      invoices: [],
      payment_frequency: 'one_time',
      payment_method: 'card',  // 'card' or 'balance'

      modalCouponFieldValue: '',
      appliedCoupon: '',

      ALLOWED_SILO_ORDER_DELIVERY_PERIODS: ALLOWED_SILO_ORDER_DELIVERY_PERIODS,
      AVAILABLE_BALANCE_TOP_UP_OPTIONS: AVAILABLE_BALANCE_TOP_UP_OPTIONS,

      contentAuditPackages: null,

      showSendInvoiceConfirmModal: false,
      showBalancePaymentConfirmModal: false,
      showSetPaidConfirmModal: false,

      showCouponInput: false,
    };
  },
  watch: {
    '$store.state.showSiloContentReportsOrdersCart': function (newVal, oldVal) {
      if (oldVal !== newVal && newVal) {
        this.loadData();
      }
      if (oldVal !== newVal && !newVal) {
        this.showCouponInput = false;
      }
    },
    '$store.state.refreshSiloContentReportsOrdersCart': function () {
      this.loadData();
    },
    appliedCoupon(val) {
      if (val) this.showCouponInput = false;
    },
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    selectedSubAccountRelation() {
      return this.$store.state.selectedSubAccountRelation;
    },
    ownerUser() {
      return this.$store.state.selectedSubAccountRelation || this.$store.state.user
    },
    isSubAccountRelationSelected() {
      return !!this.$store.state.selectedSubAccountRelation;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isMissionControlSession () {
      return this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount
    },
    isWhiteGloveUser () {
      if (!this.user) return false;

      const accountType = this.$store.state.selectedSubAccountRelation
        ?this.$store.state.selectedSubAccountRelation.ownerAccountType
        :this.user.accountType

      if (whiteGlovePlans.includes(getPureServicePkgName(accountType))) {
        return true;
      }
      return false;
    },
    AVAILAIBLE_TOP_LEVEL_SILO_REPORTS() {
      return this.packages.filter(p => p.type === 'top_level');
    },
    AVAILAIBLE_SUPPORTING_SILO_REPORTS() {
      return this.packages.filter(p => p.type === 'supporting');
    },
    sortedOrders() {
      return sortBy(this.orders, 'delivery_period_days');
    },
    ordersGroupedBySite() {
      const grouped = groupBy(this.sortedOrders, (o) => o.cartSiteLabel || 'Other');
      const keys = Object.keys(grouped).sort((a, b) => {
        const rank = (k) => (k === 'Balance' ? 2 : k === 'Other' ? 1 : 0);
        const d = rank(a) - rank(b);
        if (d !== 0) return d;
        return a.localeCompare(b);
      });
      return keys.map((label) => ({ label, orders: grouped[label] }));
    },
    cartItemCountText() {
      if (this.invoices.length) return '';
      const n = this.orders.length;
      return `${n} ${n === 1 ? 'item' : 'items'}`;
    },
    cartDeliveryNote() {
      const hasContent = this.orders.some((o) =>
        ['silo_order', 'quick_content_order'].includes(o.type)
      );
      const hasAudit = this.orders.some((o) => o.type === 'audit_order');
      const hasBalance = this.orders.some((o) => o.type === 'balance_top_up_order');
      if (hasContent && !hasAudit && !hasBalance) {
        return 'Delivered in 5–7 business days. Each article is 1,500 – 2,000 words.';
      }
      if (hasAudit && !hasContent && !hasBalance) {
        return 'Content audit delivery timelines are confirmed after purchase.';
      }
      return 'Delivery timelines vary by product; details are shown per line item above.';
    },
    siloDashboardUrl () {
      const { siteId, campaignId } = this.$route.params
      if (campaignId && siteId) {
        return `/sites/${siteId}/campaign/${campaignId}/silo`
      }
      return null
    },
    isRecurringApplicable() {
      // NOTE: recurring only available when all orders are silo or content orders
      // and have applicable delivery dates
      return (
        this.orders.filter(order => !['silo_order', 'quick_content_order'].includes(order.type)).length === 0
        && Boolean(this.orders.filter(order => !ALLOWED_SILO_ORDER_DELIVERY_PERIODS_FOR_DUE_NOW.includes(order.delivery_period_days)).length)
      )
    },
    isBalancePaymentApplicable() {
      return (
        this.orders.filter(order => !['silo_order', 'quick_content_order'].includes(order.type)).length === 0
        && this.ownerUser.balance_cents >= this.getTotalPrice()
        && this.payment_frequency === 'one_time'
      )
    }
  },
  methods: {
    formatCurrency: formatCurrency,
    getGroupSubtotal(orders) {
      return orders.reduce((sum, o) => sum + this.getOrderPrice(o), 0);
    },
    getOrderTitle(order) {
      if (['silo_order', 'quick_content_order'].includes(order.type)) {
        return order.orderLabel;
      }
      if (order.type === 'audit_order') {
        return `Audit #${order.id}`;
      }
      if (order.type === 'balance_top_up_order') {
        return 'Balance Top Up';
      }
      return '';
    },
    getOrderSubtitle(order) {
      if (['silo_order', 'quick_content_order'].includes(order.type)) {
        const parts = [];
        const top = this.getSelectedTopLevelOption(order);
        if (top && top.label) parts.push(top.label);
        const sup = this.getSelectedSupportingOption(order);
        const supCount = order?.selected_supporting_keyword_ids?.length || 0;
        if (supCount > 0 && sup && sup.label) parts.push(sup.label);
        const del = this.ALLOWED_SILO_ORDER_DELIVERY_PERIODS[order.delivery_period_days];
        if (del && del.label) parts.push(`${del.label} delivery`);
        return parts.length ? parts.join(' · ') : '—';
      }
      if (order.type === 'audit_order') {
        if (order.packageKey === 'POP_AUTHORITY_SETUP_FEE') {
          return 'POP Authority setup fee';
        }
        return order.isSpeedyDeliveryOrder ? 'Speedy delivery' : 'Standard delivery';
      }
      if (order.type === 'balance_top_up_order') {
        const pkg = this.getPackageForBalanceTopUpKey(order.key);
        const bonus =
          pkg.top_up_bonus > 0 ? ` + $${pkg.top_up_bonus} bonus` : '';
        return `Get $${pkg.top_up_amount}${bonus}`;
      }
      return '';
    },
    salesTagButtonLabel(order) {
      if (order.sales_tag === 'passive') return 'Passive';
      if (order.sales_tag === 'active') return 'Active';
      if (!order.sales_tag) return '—';
      return order.sales_tag;
    },
    getSiloUrl(siloKeyword) {
      return siloKeyword.keywordData.page || siloKeyword.keywordData.currentRankingUrl;
    },
    getSelectedTopLevelOption(order) {
      return this.AVAILAIBLE_TOP_LEVEL_SILO_REPORTS.filter(p => p.key === order.selected_top_level_option_key)[0];
    },
    getSelectedSupportingOption(order) {
      return this.AVAILAIBLE_SUPPORTING_SILO_REPORTS.filter(p => p.key === order.selected_supporting_option_key)[0];
    },
    getAuditPackageKey(order) {
      // explicit package (POP Authority setup fee) wins over the White Glove standard/speedy rule
      if (order?.packageKey && this.contentAuditPackages?.[order.packageKey]) {
        return order.packageKey
      }
      if (order?.isSpeedyDeliveryOrder) {
        return 'WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY'
      }
      return 'WHITE_GLOVE_ADDON_CONTENT_AUDIT'
    },
    getPackageForBalanceTopUpKey(key){
      return AVAILABLE_BALANCE_TOP_UP_OPTIONS.filter(op => op.key == key)[0]
    },
    getOrderPrice(order) {
      if (['silo_order', 'quick_content_order'].includes(order.type)) {
        let top_level = 0
        let supporting = 0
        let speedyDelivery = 0
        if (order?.selected_top_level_keyword_ids?.length) {
          top_level = this.getSelectedTopLevelOption(order).price * order.selected_top_level_keyword_ids.length
        }
        if (order?.selected_supporting_keyword_ids?.length) {
          supporting = this.getSelectedSupportingOption(order).price * order.selected_supporting_keyword_ids.length
        }
        if (order.delivery_period_days === SILO_ORDER_SPEEDY_DELIVERY_PERIOD) {
          speedyDelivery = (top_level + supporting) * SILO_ORDER_SPEEDY_DELIVERY_MULTIPLIER
        }
        return top_level + supporting + speedyDelivery;
      } else if (['audit_order'].includes(order.type)) {
        return this.contentAuditPackages[this.getAuditPackageKey(order)].price
      } else if (['balance_top_up_order'].includes(order.type)) {
        return this.getPackageForBalanceTopUpKey(order.key).price
      }
      return 0;
    },
    getTotalPriceBeforeDiscount() {
      return this.orders.map(order => this.getOrderPrice(order)).reduce((a, b) => a + b)
    },
    getCouponAmountOff() {
      let amountOff = 0
      if (this.appliedCoupon) {
        amountOff = this.appliedCoupon.coupon.amountOff
        if (this.appliedCoupon.coupon.couponType == 'Percent Off') {
          amountOff = (this.getTotalPriceBeforeDiscount() * amountOff / 100)
        }
      }
      return amountOff
    },
    getTotalPrice() {
      return (this.getTotalPriceBeforeDiscount() - this.getCouponAmountOff())
    },
    getDueTodayOrders() {
      const firstPeriodOrders = this.orders.filter(order => ALLOWED_SILO_ORDER_DELIVERY_PERIODS_FOR_DUE_NOW.includes(order.delivery_period_days))

      if (!firstPeriodOrders.length) {
        return 0
      }

      return firstPeriodOrders.map(order => this.getOrderPrice(order)).reduce((a, b) => a + b)
    },
    getInvoiceAmount() {
      // Recurring invoices cover only the due-now orders (delivery_period_days in
      // ALLOWED_SILO_ORDER_DELIVERY_PERIODS_FOR_DUE_NOW); due-later orders are
      // scheduled separately. One-time invoices cover the full cart total.
      return this.payment_frequency === 'recurring'
        ? this.getDueTodayOrders()
        : this.getTotalPrice()
    },
    updateOrder(order, payload) {
      this.$store.commit('showLoading');
      
      let promise
      if (order.type == 'balance_top_up_order') {
        promise = this.$store.dispatch('updateBalanceTopUpOrder', {
          orderId: order.id,
          body: payload
        })
      } else if (order.type == 'audit_order') {
        promise = this.$store.dispatch('updateSiteContentAudit', {
          auditId: order.id,
          body: payload
        })
      } else if (order.type == 'silo_order') {
        promise = this.$store.dispatch('updateSiloContentReportOrder', {
          orderId: order.id,
          body: payload
        })
      } else if (order.type == 'quick_content_order') {
        promise = this.$store.dispatch('updateSiloContentReportOrder', {
          orderId: order.id,
          body: payload
        })
      }

      promise.then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          return this.loadOrders()
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    setAllSalesTags(tag) {
      this.$store.commit('showLoading');
      Promise.all(
        this.orders.map(order => {
          if (order.type == 'balance_top_up_order') {
            return this.$store.dispatch('updateBalanceTopUpOrder', {
              orderId: order.id,
              body: { sales_tag: tag }
            })
          } else if (order.type == 'audit_order') {
            return this.$store.dispatch('updateSiteContentAudit', {
              auditId: order.id,
              body: { sales_tag: tag }
            })
          } else if (order.type == 'silo_order') {
            return this.$store.dispatch('updateSiloContentReportOrder', {
              orderId: order.id,
              body: { sales_tag: tag }
            })
          } else if (order.type == 'quick_content_order') {
            return this.$store.dispatch('updateSiloContentReportOrder', {
              orderId: order.id,
              body: { sales_tag: tag }
            })
          }
        })
      )
      .finally(() => {
        return this.loadOrders()
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    closeCartInvoice(invoice) {
      this.$store.commit('showLoading');
      
      this.$store.dispatch('closeCartInvoice', {
        cartInvoiceId: invoice.id,
      }).then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          return this.loadOrders()
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    loadOrders() {
      return this.$store.dispatch('getCartItems', {})
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.orders = response.data.data;
          if (this.payment_method === 'balance' && !this.isBalancePaymentApplicable) {
            this.payment_method = 'card'
          }
          return Promise.resolve()
        }
      }).then(() => {
        return this.$store.dispatch('getCartInvoices', {})
          .then(response => {
            if (response.data && response.data.status == 'SUCCESS') {
              this.invoices = response.data.data;
              return Promise.resolve()
            }
          })
      })
    },
    applyCoupon () {
      this.appliedCoupon = null;
      this.$store.commit('showLoading')
      return this.$store.dispatch('fetchValidatedCoupons', { promoCode: this.modalCouponFieldValue.trim(), isRegisterPage: 0 })
        .then((response) => {
          if (!response.data) {
            this.$notify({
              group: 'info', type: 'warning',
              text: 'Invalid coupon'
            })
            return;
          }
          if (response.data && response.data.status == 'SUCCESS') {
            let allServiceCoupons = response.data.coupons
            if (allServiceCoupons.length == 1) {
              const coupon = allServiceCoupons[0];
              if (['Percent Off'/*, 'Amount Off'*/].includes(coupon.couponType) && coupon.product === 'SILO_CONTENT_REPORTS_PURCHASE_CART') {
                this.appliedCoupon = {
                  code: this.modalCouponFieldValue.trim(),
                  coupon
                };
              } else {
                this.$notify({
                  group: 'info', type: 'warning',
                  text: 'Invalid coupon'
                })
              }
            } else {
              this.$notify({
                group: 'info', type: 'warning',
                text: 'Invalid coupon'
              })
            }
          } else {
            if (response.data.status == 'FAILURE') {
              this.$notify({
                group: 'info', type: 'warning',
                text: response.data.errorMsg ? response.data.errorMsg : 'Invalid coupon'
              })
            }
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    reloadUser() {
      // reload user to update ui with any credit usage
      return this.$store.dispatch("loadUser").then(response => {
        this.$store.commit("setUser", response.data.user);
      });
    },
    checkout(mode='checkout') {
      this.$store.commit('showLoading');
      this.$store.dispatch('createStripePayment', {
        packageType: 'CART_PURCHASE',
        userID: this?.selectedSubAccountRelation?.ownerId || this.user.id,
        items: this.orders.map(order => ({
          'type': order.type,
          'id': order.id
        })),
        cartCheckoutType: this.payment_frequency,
        ...this.payment_frequency === 'one_time' ? {promoCode:  this.appliedCoupon?.code} : {},
        mode,
      })
      .then((response) => {
        if (response?.data?.url) {
          window.open(response.data.url, '_self')
        } else if (response?.data?.no_immediately_due_items) {
          return this.loadOrders()
            .then(() => this.$store.commit('setShowSiloContentReportsOrdersCart', false))
        } else if (mode === 'invoice' && response.data.status === 'SUCCESS') {
          this.$notify({
            group: 'info', type: 'success',
            text: 'Invoice sent to user'
          })
          return this.loadOrders()
            .then(() => {
              this.showSendInvoiceConfirmModal = false
            })
        } else if (mode === 'balance' && response.data.status === 'SUCCESS') {
          this.$notify({
            group: 'info', type: 'success',
            text: 'Purchase successful'
          })
          this.showBalancePaymentConfirmModal = false
          return this.loadOrders()
            .then(() => this.reloadUser())
            .then(() => {
              this.$store.commit('setShowSiloContentReportsOrdersCart', false)
              this.$store.commit('triggerRefreshDataAfterCartPurchase', true);
            })
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    setAllAsPaid() {
      this.$store.commit('showLoading');
      return this.$store.dispatch('setCartItemsAsPaid')
      .then((response) => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.$notify({
            group: 'info', type: 'success',
            text: 'All orders set as paid'
          })
          return this.loadOrders()
            .then(() => this.reloadUser())
            .then(() => {
              this.$store.commit('setShowSiloContentReportsOrdersCart', false)
              this.$store.commit('triggerRefreshDataAfterCartPurchase', true);
              this.showSetPaidConfirmModal = false;
            })
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading');
      })
    },
    loadData () {
      this.loading = true;
      this.$store.dispatch('getAvailableSiloContentReportPackages')
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.packages = response.data.packages;
          return Promise.resolve()
        }
      })
      .then(() => {
        return this.$store.dispatch('getAvailableWhiteGloveAddonPackages')
          .then(response => {
            if (response.data && response.data.status == 'SUCCESS') {
              this.contentAuditPackages = Object.fromEntries(
                response.data.packages
                  .filter(p => ["WHITE_GLOVE_ADDON_CONTENT_AUDIT", "WHITE_GLOVE_ADDON_CONTENT_AUDIT_SPEEDY_DELIVERY", "POP_AUTHORITY_SETUP_FEE"].includes(p.key))
                  .map(p => [p.key, p])
              );
              return Promise.resolve()
            }
          })
      })
      .then(() => this.loadOrders())
      .finally(() => {
        this.loading = false;
      })
    }
  },
  mounted () {
    this.loadData()
  }
};
</script>

<style lang="scss" scoped>
.orders-cart-button {
  position: relative;

  .orders-cart-count {
    position: absolute;
    display: inline-block;
    top: -5px;
    right: -5px;
    background: #FF3232;
    color: #fff;
    width: 16px;
    height: 16px;
    line-height: 16px;
    text-align: center;
    border-radius: 16px;
    font-size: 10px;
    font-weight: 700;
  }
}

.silo-reports-cart-modal {
  overflow: hidden;
  color: var(--v-mainColor-base);

  .modal-content {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 100vh;
    min-height: 100vh;
    overflow: hidden;
  }

  .modal-container {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    max-height: none;
    overflow: hidden;
  }

  .cart-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 24px 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
  }

  .cart-header__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.35;
    color: var(--v-mainColor-base);
  }

  .cart-header__count {
    margin: 6px 0 0;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.45);
  }

  .cart-header__close {
    margin: -4px -8px 0 0;
    padding: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--v-mainColor-base);
    line-height: 1;

    .material-icons {
      font-size: 1.5rem;
    }
  }

  .main-part {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .orders-list {
    flex: 1;
    min-height: 0;
    padding: 8px 24px 16px;
    overflow-y: auto;
  }

  .cart-site-group {
    margin-bottom: 8px;
  }

  .cart-domain-heading {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #005b64;
    padding-top: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e0e0;
  }

  .cart-line-row {
    display: grid;
    grid-template-columns: 1fr minmax(100px, auto) auto;
    align-items: center;
    gap: 12px 16px;
    padding: 14px 0;
    border-bottom: 1px solid #e0e0e0;

    &--no-status {
      grid-template-columns: 1fr auto;
    }
  }

  .cart-line-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--v-mainColor-base);
    line-height: 1.35;
  }

  .cart-line-subtitle {
    margin-top: 4px;
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.45);
    line-height: 1.35;
  }

  .cart-line-row__status {
    justify-self: center;
  }

  .cart-line-row__price-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    justify-self: end;
  }

  .cart-line-price {
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--v-mainColor-base);
    white-space: nowrap;
  }

  .cart-line-delete {
    padding: 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #ff4d4d;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .material-icons-outlined {
      font-size: 20px;
    }
  }

  .cart-subtotal-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
    margin-bottom: 8px;
    padding: 12px 14px;
    background: #f7f7fa;
    border-radius: 4px;
    font-size: 0.9375rem;
    font-weight: 700;
  }

  .cart-form-footer {
    padding: 20px 24px 24px;
    border-top: 1px solid #e0e0e0;
    flex-shrink: 0;
  }

  .cart-bulk-sales {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 10px 12px;
    border: 1px dashed var(--v-grayColor-base);
    border-radius: 6px;
  }

  .cart-bulk-sales__label {
    font-size: 0.85rem;
  }

  .cart-bulk-sales__sep {
    color: rgba(0, 0, 0, 0.35);
  }

  .cart-recurring-block {
    margin-bottom: 20px;
  }

  .cart-section-label {
    font-weight: 700;
    margin-bottom: 12px;
    font-size: 0.9375rem;
  }

  .cart-total-section {
    margin-bottom: 16px;
  }

  .cart-total-section__count {
    margin: 0 0 8px;
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.45);
  }

  .cart-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    font-weight: 700;

    &--secondary {
      margin-top: 8px;
      font-size: 0.9375rem;
      font-weight: 600;
    }
  }

  .cart-total-section__rule {
    margin-top: 14px;
    height: 1px;
    background: #e0e0e0;
  }

  .cart-footer-note {
    margin: 0 0 20px;
    text-align: center;
    font-size: 0.8125rem;
    color: rgba(0, 0, 0, 0.45);
    line-height: 1.45;
  }

  .cart-balance-block {
    margin-bottom: 16px;
  }

  .payment-frequency-options {
    display: flex;
    flex-direction: row;
    gap: 12px;

    ::v-deep .b-radio.radio {
      flex: 1;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 12px;
      font-weight: bold;
      margin: 0;
    }
  }

  .payment-method-options {
    display: flex;
    flex-direction: column;
    gap: 12px;

    ::v-deep .b-radio.radio {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 12px;
      font-weight: bold;
      margin-left: 0;
    }
  }

  .cart-primary-btn {
    height: 52px !important;
    border-radius: 8px !important;
    background-color: #ff5c00 !important;
    border-color: #ff5c00 !important;
    color: #fff !important;
    box-shadow: none !important;

    .v-btn__content {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .v-btn__content span {
      font-size: 1rem;
      font-weight: 700;
    }
  }

  .cart-primary-btn__icon {
    font-size: 1.25rem !important;
  }

  .cart-coupon-block {
    margin-top: 16px;
    text-align: center;
  }

  .cart-coupon-link {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #2b63e1;
    text-decoration: none;
  }

  .cart-coupon-link:hover {
    text-decoration: underline;
  }

  .cart-coupon-applied {
    margin: 0;
    font-size: 0.875rem;
    color: #11A33D;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .cart-coupon-applied__icon {
    font-size: 1.125rem;
  }

  .cart-coupon-fields {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    max-width: 400px;
    margin: 0 auto;
  }

  .cart-coupon-fields__input {
    flex: 1;
  }

  .cart-coupon-fields__apply {
    margin-top: 2px;
    min-width: 88px;
    background-color: #2b63e1 !important;
    border-color: #2b63e1 !important;
    color: #fff !important;
    box-shadow: none !important;
  }

  ::v-deep .cart-sales-tag-dropdown .dropdown-trigger {
    width: 100%;
  }

  ::v-deep .cart-sales-tag-dropdown .dropdown-menu {
    min-width: 160px;
  }

  .cart-sales-tag-trigger {
    min-width: 108px;
    height: 36px;
    padding: 0 10px 0 12px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--v-mainColor-base);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
  }

  .cart-sales-tag-trigger__caret {
    font-size: 1.125rem !important;
    color: rgba(0, 0, 0, 0.45);
  }
}

::v-deep {
  .b-radio.custom-radio-color, .b-radio.custom-radio-color:hover {
    input[type="radio"]:checked, input[type="radio"]:hover  {
      &+ .check {
        border-color: #FF5700 !important;
        &::before {
          background-color: #FF5700 !important;
        }
      }
    }
  }
}

.empty-cart {
  display: flex;
  flex-direction: column;
  flex: 1;
  color: var(--v-mainColor-base);

  .empty-cart-main {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    text-align: center;
    padding: 60px 70px 80px;

    h3 {
      font-size: 1.3rem;
      font-weight: 700;
      line-height: 1.7rem;
    }

    p {
      font-size: 1.1rem;
      font-weight: 400;
      line-height: 1.6rem;
    }
  }

  .empty-cart-form-controls {
    padding: 20px;
    border-top: 1px solid #CCCCCC;

    button {
      height: 50px;
      border-radius: 50px;

      .v-btn__content span {
        font-size: 1.1rem;
        font-weight: 700;
        line-height: 1.5rem;
      }
    }
  }
}

.loading-cart {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .div {
    text-align: center;
  }
}

.sales-tag-dropdown {
  .dropdown-item {
    font-weight: normal;
  }

  &.is-disabled {
    opacity: initial;
    cursor: default;
  }
}

.sales-tag-btn {
  text-transform: initial;
  letter-spacing: initial;
}
</style>
